import { AxQuizz } from '@theme/ax-quizz';
import { pickResults, portionShares, shakerShare, shakerMax } from '@theme/ax-quizz-matching';
import { cappedCoefficient } from '@theme/ax-quizz-formula';

const PRODUCT_SECTION_ID = 'ax-quizz-mo-product';

class AxQuizzMetaobject extends AxQuizz {
  #matching = { reponses: {}, portions: {}, produits: {}, bundles: {} };
  #portionState = { keys: [], total: 0, dose: null, shakers: 1 };
  #availability = {};
  #resultTemplate = '';
  #fetchController = null;
  #resultReady = Promise.resolve();
  #portionSummary = { shaker: null, aliments: [] };
  #productSummary = { primary: null, secondary: null };
  #openHash = '';
  #onHashChange = null;

  connectedCallback() {
    super.connectedCallback();

    this.resultRoot = this.querySelector('.ax-quizz__result');
    this.#resultTemplate = this.resultRoot?.innerHTML ?? '';
    this.#matching = this.#readMatching();
    this.#availability = Object.fromEntries(
      Object.entries(this.#matching.produits).map(([handle, entry]) => [handle, entry?.available !== false])
    );

    for (const checkbox of this.querySelectorAll('.ax-quizz__consent-checkbox')) {
      checkbox.addEventListener('change', () => {
        if (!checkbox.checked) return;
        checkbox.closest('.ax-quizz__consent')?.classList.remove('is-invalid');
        checkbox.removeAttribute('aria-invalid');
      });
    }

    this.#openHash = (this.dataset.openHash ?? '').trim().replace(/^#/, '');
    if (!this.#openHash) return;
    this.#onHashChange = () => this.#openFromHash();
    window.addEventListener('hashchange', this.#onHashChange);
    this.#openFromHash();
  }

  #openFromHash() {
    let hash;
    try {
      hash = decodeURIComponent(window.location.hash.slice(1));
    } catch {
      return;
    }
    if (hash !== this.#openHash) return;
    history.replaceState(history.state, '', window.location.pathname + window.location.search);
    this.open();
  }

  _validateStep(stepIndex) {
    let valid = super._validateStep(stepIndex);
    const step = this.steps[stepIndex];
    if (!step) return valid;

    for (const checkbox of step.querySelectorAll('.ax-quizz__consent-checkbox')) {
      if (checkbox.checked) continue;
      checkbox.closest('.ax-quizz__consent')?.classList.add('is-invalid');
      checkbox.setAttribute('aria-invalid', 'true');
      valid = false;
    }

    return valid;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#fetchController?.abort();
    if (this.#onHashChange) window.removeEventListener('hashchange', this.#onHashChange);
  }

  #readMatching() {
    const source = this.querySelector('script[data-quizz-matching]');
    if (!source) return this.#matching;
    try {
      const parsed = JSON.parse(source.textContent);
      return {
        reponses: parsed.reponses ?? {},
        portions: parsed.portions ?? {},
        produits: parsed.produits ?? {},
        bundles: parsed.bundles ?? {},
      };
    } catch (error) {
      console.error('ax-quizz-metaobject: invalid matching JSON', error);
      return this.#matching;
    }
  }

  _getValues() {
    const values = super._getValues();
    const formula = this.dataset.coefficientFormula;
    if (!formula) return values;

    const coefficient = cappedCoefficient(formula, values.calc, this.dataset.coefficientMax);
    if (coefficient === null) return values;

    values.calc.coefficient = String(coefficient);
    values.param.coefficient = String(coefficient);
    return values;
  }

  _showResult(total, values) {
    if (this.resultRoot) this.resultRoot.innerHTML = this.#resultTemplate;

    const numericTotal = Number.isFinite(total) ? total : 0;
    const preserved = { ...values, shaker: '[shaker]', pourcentage: '[pourcentage]' };

    super._showResult(total, preserved);
    if (!this.resultRoot) return;

    const picked = this.#pick();
    const keyValues = { ...preserved, total: this._formatNumber(numericTotal) };

    this.#portionState = { keys: picked.portions, total: numericTotal, dose: null, shakers: 1 };
    this.#renderPortions();
    this.#resultReady = this.#renderProducts(picked, keyValues, numericTotal);
  }

  async _sendToKlaviyo(result, paramValues, onDone) {
    onDone();
    await this.#resultReady;
    super._sendToKlaviyo(result, paramValues, () => {});
  }

  _klaviyoProperties() {
    const { shaker, shakers, aliments } = this.#portionSummary;
    return {
      Prot_shaker_grammes: shaker,
      Prot_shakers: shakers,
      Prot_aliments: aliments,
      Prot_aliments_texte: aliments.map(({ texte }) => texte).join(' + '),
      Prot_product1: this.#productSummary.primary,
      Prot_product2: this.#productSummary.secondary,
    };
  }

  #pick() {
    const productLists = [];
    const portionLists = [];
    const preferredLists = [];

    for (const select of this.querySelectorAll('.ax-quizz__select')) {
      const handle = select.selectedOptions[0]?.dataset.reponse;
      const response = handle ? this.#matching.reponses[handle] : null;
      if (!response) continue;
      const priority = parseInt(select.dataset.priority, 10);
      if (response.produits?.length) productLists.push({ keys: response.produits, priority });
      if (response.portions?.length) portionLists.push({ keys: response.portions, priority });
      if (response.prioritaires?.length) preferredLists.push({ keys: response.prioritaires, priority });
    }

    const tier = (priority) => (Number.isFinite(priority) ? priority : 100);
    const preferred = preferredLists.sort((a, b) => tier(a.priority) - tier(b.priority)).flatMap((entry) => entry.keys);

    return pickResults({
      productLists,
      portionLists,
      preferred,
      pageProduct: this.dataset.pageProduct || null,
      availability: this.#availability,
      bundles: this.#matching.bundles,
      portionsMax: parseInt(this.dataset.portionsMax, 10) || 4,
    });
  }

  #renderPortions() {
    const { keys, total, dose } = this.#portionState;
    const wrapper = this.resultRoot.querySelector('[data-quizz-portions]');
    const grid = wrapper?.querySelector('.ax-quizz__nutrition-grid');
    if (!wrapper || !grid) return;

    const max = parseInt(this.dataset.portionsMax, 10) || 4;
    const label = this.dataset.shakerPortionLabel;
    const shakerGrams = parseFloat(dose);
    const withShaker = Boolean(label) && Number.isFinite(shakerGrams) && shakerGrams > 0;

    const selected = keys
      .map((key) => this.#matching.portions[key])
      .filter(Boolean)
      .slice(0, withShaker ? max - 1 : max);

    const items = [];
    const aliments = [];

    const shakerLimit = withShaker
      ? Math.min(shakerMax(shakerGrams, total), parseInt(this.dataset.shakerMax, 10) || Infinity)
      : 1;
    const shakers = Math.min(this.#portionState.shakers, shakerLimit);
    this.#portionState.shakers = shakers;
    const shakerTotal = withShaker ? shakers * shakerGrams : 0;

    if (withShaker) {
      const template = shakers > 1 ? this.dataset.shakerPortionLabelPlural || label : label;
      const text = this._replaceKeys(template, {
        nombre: String(shakers),
        grammes: this._formatNumber(Math.round(shakerTotal)),
      });
      const cut = text.indexOf('|');
      const item =
        cut > 0
          ? this.#portionItem(this.dataset.shakerPortionIcon, text.slice(0, cut).trim(), text.slice(cut))
          : this.#portionItem(this.dataset.shakerPortionIcon, text);
      item.appendChild(this.#shakerStepper(shakers, shakerLimit));
      items.push(item);
    }

    const remaining = Math.max(total - shakerTotal, 0);
    for (const { portion, grams, count } of portionShares(selected, remaining)) {
      if (grams === null) continue;
      const nom = portion.label ?? '';
      const unite = count === null ? 'g' : count > 1 ? portion.unitePluriel || portion.unite : portion.unite;
      const quantite = count ?? grams;
      const text = count === null ? nom : `${count} ${unite}`;
      const suffix = count === null ? `| ${this._formatNumber(grams)} g` : '';
      items.push(this.#portionItem(portion.image, text, suffix));
      aliments.push({
        nom,
        quantite,
        unite,
        grammes: grams,
        texte: count === null ? `${grams} g de ${nom.toLowerCase()}` : text,
      });
    }

    this.#portionSummary = {
      shaker: withShaker ? Math.round(shakerTotal) : null,
      shakers: withShaker ? shakers : null,
      aliments,
    };

    grid.replaceChildren(...items);
    wrapper.hidden = items.length === 0;
  }

  #shakerStepper(count, max) {
    const stepper = document.createElement('div');
    stepper.className = 'ax-quizz__stepper';

    for (const [step, icon] of [
      [-1, '<path d="M0 .5h8" stroke="currentColor"/>'],
      [1, '<path d="M0 4h8M4 0v8" stroke="currentColor"/>'],
    ]) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ax-quizz__stepper-button';
      button.dataset.step = String(step);
      button.disabled = step < 0 ? count <= 1 : count >= max;
      button.setAttribute('aria-label', (step < 0 ? this.dataset.shakerLess : this.dataset.shakerMore) ?? '');
      button.innerHTML = `<svg width="8" height="${step < 0 ? 1 : 8}" viewBox="0 0 8 ${step < 0 ? 1 : 8}" fill="none" aria-hidden="true">${icon}</svg>`;
      button.addEventListener('click', () => this.#stepShakers(step));
      stepper.appendChild(button);
    }

    return stepper;
  }

  #stepShakers(step) {
    this.#portionState.shakers += step;
    this.#renderPortions();

    const buttons = [...this.resultRoot.querySelectorAll('.ax-quizz__stepper-button')];
    const target = buttons.find((button) => button.dataset.step === String(step) && !button.disabled);
    (target ?? buttons.find((button) => !button.disabled))?.focus();

    const live = this.resultRoot.querySelector('[data-quizz-portions-live]');
    if (live) {
      live.textContent = [...this.resultRoot.querySelectorAll('.ax-quizz__nutrition-text')]
        .map((element) => element.textContent)
        .join(' + ');
    }
  }

  #portionItem(icon, text, suffix = '') {
    const item = document.createElement('div');
    item.className = 'ax-quizz__nutrition-item';

    if (icon) {
      const image = document.createElement('img');
      image.className = 'ax-quizz__nutrition-icon';
      image.src = icon;
      image.alt = '';
      image.width = 44;
      image.height = 44;
      image.loading = 'lazy';
      item.appendChild(image);
    }

    const span = document.createElement('span');
    span.className = 'ax-quizz__nutrition-text';
    span.textContent = text;
    if (suffix) {
      const nowrap = document.createElement('span');
      nowrap.textContent = suffix;
      span.append(' ', nowrap);
    }
    item.appendChild(span);

    return item;
  }

  async #renderProducts(picked, keyValues, total) {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();
    const { signal } = this.#fetchController;
    this.#productSummary = { primary: null, secondary: null };

    const primarySlot = this.resultRoot.querySelector('[data-quizz-slot="primary"]');
    const secondarySlot = this.resultRoot.querySelector('[data-quizz-slot="secondary"]');

    const [primaryBody, secondaryBody] = await Promise.all([
      this.#fetchProduct(picked.primary, signal),
      this.#fetchProduct(picked.secondary, signal),
    ]);
    if (signal.aborted) return;

    if (this.#mount(primarySlot, primaryBody)) {
      const titleEl = primarySlot.querySelector('.ax-quizz__r-product-title');
      if (titleEl && titleEl.textContent.trim() === '') {
        titleEl.textContent = this.#productTitle(primaryBody);
      }
      this.#setupShaker(primarySlot, primaryBody, total);
      this.#productSummary.primary = this.#productRef(primaryBody);
    }

    if (this.#mount(secondarySlot, secondaryBody)) {
      this.#applySecondary(secondarySlot, secondaryBody, picked, keyValues);
      this.#productSummary.secondary = this.#productRef(secondaryBody);
    }

    document.dispatchEvent(new CustomEvent('reactivateLazyObserver'));
  }

  #productRef(body) {
    const { quizzProduct: handle, quizzProductId: id } = body.dataset;
    return handle ? { handle, id: Number(id) } : null;
  }

  #pageVariantId() {
    const fromUrl = new URL(window.location.href).searchParams.get('variant');
    return fromUrl || this.dataset.pageVariant || '';
  }

  async #fetchProduct(handle, signal) {
    if (!handle) return null;

    const root = window.Shopify?.routes?.root ?? '/';
    let url = `${root}products/${encodeURIComponent(handle)}?section_id=${PRODUCT_SECTION_ID}`;

    const variantId = handle === this.dataset.pageProduct ? this.#pageVariantId() : '';
    if (variantId) url += `&variant=${encodeURIComponent(variantId)}`;

    try {
      const response = await fetch(url, { signal });
      if (!response.ok) return null;
      const fragment = document.createRange().createContextualFragment(await response.text());
      return fragment.querySelector('.ax-quizz__r-product-body');
    } catch (error) {
      if (error.name !== 'AbortError') console.error('ax-quizz-metaobject: product fetch failed', error);
      return null;
    }
  }

  #mount(slot, body) {
    if (!slot) return false;
    if (!body) {
      slot.hidden = true;
      return false;
    }
    slot.querySelector('.ax-quizz__r-product-mount')?.replaceChildren(body);
    slot.hidden = false;
    return true;
  }

  #setupShaker(slot, body, total) {
    const wrapper = this.resultRoot.querySelector('.ax-quizz__shaker');
    const targets = [
      slot.querySelector('.ax-quizz__r-product-title'),
      this.resultRoot.querySelector('[data-quizz-shaker-message]'),
      this.resultRoot.querySelector('[data-quizz-shaker-note]'),
    ]
      .filter(Boolean)
      .map((element) => ({ element, template: element.textContent }));

    let doses = {};
    const source = body.querySelector('script[data-quizz-doses]');
    if (source) {
      try {
        doses = JSON.parse(source.textContent) || {};
      } catch (error) {
        console.error('ax-quizz-metaobject: invalid doses JSON', error);
      }
    }

    const update = () => {
      const dose = doses[body.querySelector('input[name="id"]')?.value ?? ''];
      const share = shakerShare(dose, total);
      const replacements = {
        shaker: share === null ? '' : String(dose).replace('.', ','),
        pourcentage: share === null ? '' : String(share),
      };

      for (const { element, template } of targets) {
        element.textContent = this._replaceKeys(template, replacements);
      }
      if (wrapper) wrapper.hidden = share === null;
      this.#portionState.dose = dose;
      this.#renderPortions();
    };

    body.addEventListener('change', update);
    update();
  }

  #productTitle(body) {
    return body.querySelector('.ax-quizz__product-title')?.textContent.trim() ?? '';
  }

  #applySecondary(slot, body, picked, keyValues) {
    const isBundle = picked.secondaryKind === 'bundle';
    const eyebrow = isBundle ? this.dataset.secondaryBundleEyebrow : this.dataset.secondaryProductEyebrow;
    const title = isBundle ? this.dataset.secondaryBundleTitle : this.dataset.secondaryProductTitle;

    const values = { ...keyValues, shaker: '', pourcentage: '' };

    const eyebrowEl = slot.querySelector('.ax-quizz__r-product-eyebrow');
    if (eyebrowEl) eyebrowEl.textContent = this._replaceKeys(eyebrow ?? '', values);

    const titleEl = slot.querySelector('.ax-quizz__r-product-title');
    if (titleEl) titleEl.textContent = this._replaceKeys(title || this.#productTitle(body), values);

    if (!isBundle) return;

    const link = body.querySelector('.ax-quizz__bundle-link');
    if (link) {
      const label = link.querySelector('.ax-quizz__bundle-link-label');
      if (label) label.textContent = this.dataset.secondaryBundleButton || 'Voir le pack';
      link.hidden = false;
    }
    body.querySelector('.ax-quizz__product-atc-bar')?.setAttribute('hidden', '');
  }
}

customElements.get('ax-quizz-metaobject') || customElements.define('ax-quizz-metaobject', AxQuizzMetaobject);
